import React, { useCallback, useEffect, useState } from "react";
import styled from "styled-components";

import { gql } from "@examspark/api";
import {
  Button,
  ButtonLoadingIcon,
  FlexBox,
  HSpace,
  Text,
  Toolbar,
  useVolatileRef,
} from "@examspark/common-ui";
import { IconTrashLine } from "@examspark/common-ui/dist/components/icons";
import {
  ExamConfigData,
  GoogleClassroomAssignment,
  ParsedAppUserConfig,
  ParsedClassroom,
  Resource,
} from "@examspark/types";

import { logger } from "../../../..";
import { useGoogleClassroom } from "../../../../hooks/useGoogleClassroom";
import { useGoogleSignIn } from "../../../../hooks/useGoogleSignIn";
import GoogleConnect from "../../google/GoogleConnect";

const Styled = styled.div`
  padding: 0.5rem;

  h2 {
    margin-top: 0;
    font-weight: 500;
    color: #333;
    margin-bottom: 1rem;
  }
  h3 {
    text-transform: uppercase;
    font-weight: normal;
    color: #555;
    font-size: 1.2rem;
  }
`;

const Loading = styled(FlexBox)`
  gap: 1rem;
  align-items: center;
  justify-content: center;
  margin: 3rem;
  background-color: #f7f7f7;
  padding: 3rem;
`;
const LoadingIcon = styled(FlexBox)`
  svg {
    display: block;
    width: 16px;
  }
`;
const LoadingLabel = styled(FlexBox)`
  color: #666;
`;

const SelectCourses = styled.div`
  margin: 2rem 0;
`;
const CourseListItem = styled(HSpace)`
  background-color: #f7f7f7;
  transition: background-color 0.2s;
  padding: 1rem;
  gap: 1rem;
  align-items: center;
  cursor: pointer;
  margin: 0.1rem 0;

  &:hover {
    background-color: #f0f0f0;
  }
`;
const CourseListItemName = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
`;
const CourseListItemSection = styled.div`
  text-align: right;
  color: #666;
`;

const Assigned = styled.span`
  padding: 0.4rem 0.7rem;
  border-radius: 2rem;
  background-color: #6faf6f;
  color: white;
  margin-left: 1rem;
  font-size: 1rem;
  text-transform: uppercase;

  &:hover {
    background-color: #649e64;
  }
`;

export interface AssignActivityModalProps {
  appKey: string;
  userId: string;
  classroom?: ParsedClassroom;
  examConfig?: Resource;
  onPublish?: () => void;
}

const AssignActivityGoogleClassroom: React.FC<
  React.PropsWithChildren<AssignActivityModalProps>
> = (props) => {
  const gsi = useGoogleSignIn({
    tokenServerUrl: process.env.REACT_APP_REST_API_URL || "",
  });
  const gc = useGoogleClassroom({
    tokenServerUrl: process.env.REACT_APP_REST_API_URL || "",
  });
  const [courses, setCourses] = useState<gapi.client.classroom.Course[]>();
  const [selectedCourseIds, setSelectedCourseIds] = useState<string[]>([]);
  const [isCreating, setIsCreating] = useState(false);
  const [isUnassigning, setIsUnassigning] = useState(false);
  const [isLoadingCourses, setIsLoadingCourses] = useState(false);
  const [appUserConfig, setAppUserConfig] =
    useState<ParsedAppUserConfig | null>();
  const [examConfigData, setExamConfigData] = useState<ExamConfigData>();
  const [isConnected, setIsConnected] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const [needsLogin, setNeedsLogin] = useState(false);

  const examConfigRef = useVolatileRef(props.examConfig);

  const fetchExamConfigData = useCallback(async () => {
    if (!props.examConfig?.id) return;
    const data = await gql.operations.exam.readExamConfigConfig(
      props.examConfig.id,
    );
    setExamConfigData(data);
  }, [props.examConfig]);

  const fetchCourses = useCallback(async () => {
    if (!isConnected) return;
    // console.log("fetching courses");
    setSelectedCourseIds([]);
    setIsLoadingCourses(true);
    const result = await gc.fetchCourses();
    logger.info("Fetched user courses from Google Classroom", {
      courses: result,
    });

    const filteredCourses = result?.filter((c) => {
      if (!props.classroom) return true;
      return c.id === props.classroom.data?.googleClassroom.course.id;
    });

    setCourses(filteredCourses);
    setIsLoadingCourses(false);
  }, [gc, isConnected, props.classroom]);

  const fetchAppUserConfig = useCallback(async () => {
    // console.log("fetching ap");
    logger.info("Fetching app-user-config", {
      userId: props.userId,
      appKey: props.appKey,
    });
    const config = await gql.operations.app.getUserConfig(
      props.appKey,
      props.userId,
    );

    if (!config) {
      logger.info("could not find user config");
    }

    setAppUserConfig(config);
    if (!(config as any)?.config.googleClassroom?.auth?.refresh_token) {
      setNeedsLogin(true);
      setIsConnecting(false);
      return;
    }

    try {
      setAppUserConfig(config);
      const refreshToken = (config?.config as any)?.googleClassroom?.auth
        .refresh_token;

      setIsConnecting(true);
      const accessToken = await gsi.refreshAccessToken(refreshToken);
      gsi.verifyScopes(gsi.scopes.classroom, accessToken.scope.split(" "));
      // console.log("refresh!", accessToken);
      // console.log("granted all?", grantedAllScopes);
      gc.setToken(accessToken.access_token);
      setIsConnected(true);
      setIsConnecting(false);
    } catch (error) {
      setNeedsLogin(true);
      setIsConnecting(false);
    }
  }, [gsi, gc, props.appKey, props.userId]);

  const deleteFromCourse = useCallback(
    async (courseId: string) => {
      setIsUnassigning(true);
      if (!props.examConfig?.id) return;
      const examConfig = await gql.operations.exam.readExamConfigConfig(
        props.examConfig.id,
      );
      if (examConfig === undefined) return;
      // remove google classroom assignment from `exam-config`
      if (!examConfig.googleClassroomAssignments?.length) return;
      const findIndex = examConfig.googleClassroomAssignments.findIndex(
        (gca) => gca.course.id === courseId,
      );
      if (findIndex === -1) return;
      const courseWorkId =
        examConfig.googleClassroomAssignments[findIndex].courseWorkId;
      console.log("deleting", courseWorkId);

      if (findIndex !== -1) {
        examConfig.googleClassroomAssignments.splice(findIndex, 1);
      }
      await gql.operations.exam.setExamConfig(props.examConfig.id, examConfig);
      await gc.deleteCourseWork(courseId, courseWorkId);
      logger.info("Removed activity from google classroom course", {
        assignment: examConfig.googleClassroomAssignments[findIndex],
      });
      fetchExamConfigData();
      setIsUnassigning(false);
    },
    [fetchExamConfigData, gc, props.examConfig?.id],
  );

  const { onPublish } = props;

  const assignToCourses = useCallback(async () => {
    if (!examConfigRef.current) {
      console.error("No props.examConfigKey");
      return;
    }
    if (!examConfigRef.current?.resource?.id) {
      console.error("No props.examConfig.resource.id");
      return;
    }
    let errors = false;

    setIsCreating(true);
    const examConfig = await gql.operations.exam.readExamConfigConfig(
      examConfigRef.current.id,
    );
    if (examConfig === undefined) {
      setIsCreating(false);
      return;
    }

    const gcAssignments: GoogleClassroomAssignment[] =
      examConfig.googleClassroomAssignments || [];
    await Promise.all(
      selectedCourseIds.map(async (courseId) => {
        if (!examConfigRef.current) {
          console.error("No props.examConfig");
          return;
        }
        if (!courses?.length) return;
        // do not allow coursework to be published to the same course twice
        if (gcAssignments.find((gca) => gca.course.id === courseId)) {
          logger.info("Skip assigning activity to course.", {});
          return;
        }
        const courseData = courses.find((c: any) => c.id === courseId);
        if (!courseData || !courseData.id || !courseData.name) return;

        try {
          const courseWork = await gc.createCourseWork(courseId, {
            title: examConfigRef.current.resource?.name,
            description: examConfigRef.current.resource?.description,
            examConfigId: examConfigRef.current.id,
          });

          if (!courseWork || !courseWork?.id) return;
          console.log("cousework?", courseWork);
          logger.info("Added activity to Google Classroom course", {
            course: {
              courseId,
              title: examConfigRef.current.resource?.name,
              description: examConfigRef.current.resource?.description,
              examConfigId: examConfigRef.current.id,
            },
          });
          gcAssignments.push({
            courseWorkId: courseWork.id,
            course: {
              id: courseData.id,
              name: courseData.name,
              section: courseData.section,
              room: courseData.room,
            },
          });
        } catch (error: any) {
          errors = true;
          alert(`${courseData.name}: ${error.message}`);
          logger.error("Failure adding activity to Google Classroom course", {
            course: courseData,
            error: error.message,
          });
        }
      }),
    );

    examConfig.googleClassroomAssignments = gcAssignments;
    logger.info(`Saving exam-config ${examConfigRef.current.id}`, {
      examConfig,
    });
    await gql.operations.exam.setExamConfig(
      examConfigRef.current.id,
      examConfig,
    );

    await fetchExamConfigData();
    setIsCreating(false);
    setSelectedCourseIds([]);
    if (!errors) {
      alert("Activities have been successfully published to Google Classroom.");
      onPublish?.();
    } else {
      logger.error("Saving coursework to Google Classroom failed.", {});
      alert(
        "The operation completed with some errors. Some of your activities may not have been published.",
      );
    }
  }, [
    courses,
    fetchExamConfigData,
    gc,
    examConfigRef,
    selectedCourseIds,
    onPublish,
  ]);

  useEffect(() => {
    fetchCourses();
  }, [fetchCourses]);

  useEffect(() => {
    fetchAppUserConfig();
    fetchExamConfigData();
  }, [fetchAppUserConfig, fetchExamConfigData]);

  // console.log("exam config data", examConfigData);

  return (
    <Styled>
      <Text.Body>
        Students will be able to complete this assignment directly in Google
        Classroom®.
      </Text.Body>
      {(appUserConfig === undefined || isConnecting) && (
        <Loading>
          <LoadingIcon>
            <ButtonLoadingIcon />
          </LoadingIcon>
          <LoadingLabel>
            Checking for a connection to Google Classroom...
          </LoadingLabel>
        </Loading>
      )}
      {!!needsLogin && (
        <GoogleConnect
          appKey={props.appKey}
          userId={props.userId}
          onConnect={async () => {
            fetchAppUserConfig();
            setNeedsLogin(false);
          }}
        />
      )}
      {isConnected && (
        <>
          <SelectCourses>
            <h3>Select Courses</h3>
            {courses?.map((cls: any) => {
              const isAlreadyAssigned =
                examConfigData?.googleClassroomAssignments?.find(
                  (gca) => gca.course.id === cls.id,
                );

              return (
                <CourseListItem
                  key={cls.id}
                  onClick={() => {
                    if (isAlreadyAssigned) return;
                    console.log(selectedCourseIds.includes(cls.id));

                    if (selectedCourseIds.includes(cls.id)) {
                      const _new = [...selectedCourseIds];
                      _new.splice(
                        selectedCourseIds.findIndex((c) => c === cls.id),
                        1,
                      );
                      setSelectedCourseIds(_new);
                    } else {
                      const _new = [...selectedCourseIds];
                      _new.push(cls.id);
                      setSelectedCourseIds(_new);
                    }
                  }}
                >
                  <input
                    disabled={!!isAlreadyAssigned}
                    type="checkbox"
                    value={cls.id}
                    checked={selectedCourseIds.includes(cls.id)}
                    onChange={() => {
                      // nothing
                    }}
                  />
                  <CourseListItemName>
                    <div>{cls.name} </div>
                    {isAlreadyAssigned && (
                      <Toolbar>
                        <Assigned>Assigned</Assigned>
                        <Button
                          variant="plain"
                          title="Unassign"
                          icon={<IconTrashLine />}
                          isLoading={isUnassigning}
                          onClick={(e) => {
                            e.stopPropagation();
                            if (
                              window.confirm(
                                "Unassign/Delete from this course?",
                              )
                            ) {
                              deleteFromCourse(cls.id);
                            }
                          }}
                        />
                      </Toolbar>
                    )}
                  </CourseListItemName>
                  <CourseListItemSection>
                    Section {cls.section}
                  </CourseListItemSection>
                </CourseListItem>
              );
            })}
          </SelectCourses>
          <Toolbar>
            <Button
              title=""
              isLoading={isCreating}
              disabled={selectedCourseIds.length === 0}
              variant="contained"
              color="create"
              onClick={() => assignToCourses()}
            >
              {isCreating
                ? "Publish to Selected Courses. This may take a moment."
                : "Publish to Selected Courses"}
            </Button>
            <Button
              title=""
              isLoading={isLoadingCourses}
              variant="contained"
              onClick={() => {
                fetchCourses();
              }}
            >
              {isLoadingCourses ? "Loading Courses..." : "Reload Courses"}
            </Button>
          </Toolbar>
        </>
      )}
    </Styled>
  );
};

export default AssignActivityGoogleClassroom;
